import moment from "moment"

import Visitable from "@Modules/shared/interfaces/visitable.ts"
import PriceModel, { type Price } from "@Modules/shared/Data/Price"
import type { Feature, Option } from "@Resources/js/laravel.d.ts"
import type { BillingPeriod } from "@Modules/subscriptions/Api/SubscriptionsApi"

/**
 * Raw subscription data from the server
 */
export interface SubscriptionData {
  type: string
  name: string
  billing_type: number
  billing_status: string
  quantity: number | null
  trial_ends_at: string | null
  paused_at: string | null
  ends_at: string | null
  period_starts_at: string | null
  period_ends_at: string | null
  enterprise: boolean
  interval: BillingPeriod | null
  amount: number | null
  currency: string | null
  created_at: string | null
  updated_at: string | null
  pm_type?: string | null
  pm_last_four?: string | null
  pm_expiration?: string | null
  highlighted?: string[]
  features?: Feature[]
  options?: Record<string, number | string | null>
}

/**
 * Subscription smart object with helper methods
 */
export interface Subscription extends SubscriptionData, Visitable<Subscription> {
  highlighted: string[]
  features: Feature[]
  price: Price
  isEnterprise: () => boolean
  isPastDue: () => boolean
  isOnGracePeriod: () => boolean
  isTrialing: () => boolean
  isFree: () => boolean
  isActive: () => boolean
  isIncomplete: () => boolean
  needsAction: () => boolean
  periodEndsAt: () => moment.Moment | null
  periodStartsAt: () => moment.Moment | null
  trialEndsAt: () => moment.Moment | null
  endsAt: () => moment.Moment | null
  nextRenewalDate: () => string | null
  endDate: () => string | null
  remainingTrialDays: () => number | null
  getGroup: () => string | null
  can: (feature: Feature) => boolean
  getOption: (option: Option) => number | string | null
}

/**
 * Default subscription data for free users
 */
export const FREE_SUBSCRIPTION_DATA: SubscriptionData = {
  type: "default",
  name: "free",
  billing_type: 0,
  billing_status: "active",
  quantity: null,
  trial_ends_at: null,
  paused_at: null,
  ends_at: null,
  period_starts_at: null,
  period_ends_at: null,
  enterprise: false,
  interval: null,
  amount: null,
  currency: null,
  created_at: null,
  updated_at: null,
  highlighted: [],
  features: [],
  options: {},
}

/**
 * Default visit behavior - navigate to subscription settings
 */
const VisitSubscription = (
  subscription: Subscription,
  orgSlug: string | null,
  query: Record<string, unknown> = {},
): void => {
  const theRouter = window.$router

  if (orgSlug) {
    theRouter.push({
      name: "org.settings.profile.subscription",
      params: { org: orgSlug },
      query,
    })
  } else {
    theRouter.push({
      name: "settings.subscription",
      query,
    })
  }
}

/**
 * Creates a Subscription smart object from raw server data.
 *
 * @param data The raw subscription data from the server (null for free users)
 * @param orgSlug Optional organization slug for visit navigation
 */
export default function SubscriptionModel(
  data: SubscriptionData | null,
  orgSlug: string | null = null,
): Subscription {
  const subscriptionData = data ?? FREE_SUBSCRIPTION_DATA

  let visitOverride: (model: Subscription, query: Record<string, unknown>) => void = (
    model,
    query,
  ) => VisitSubscription(model, orgSlug, query)

  // Create price object from amount/currency
  const price = PriceModel(
    subscriptionData.amount !== null && subscriptionData.currency !== null
      ? { amount: subscriptionData.amount, currency: subscriptionData.currency }
      : null,
  )

  const that: Subscription = {
    ...subscriptionData,
    highlighted: subscriptionData.highlighted ?? [],
    features: subscriptionData.features ?? [],
    price,

    /**
     * Returns true if the subscription has the given feature
     */
    can(feature: Feature): boolean {
      return this.features.includes(feature)
    },

    /**
     * Returns the value of the given option, or null if not set
     */
    getOption(option: Option): number | string | null {
      return subscriptionData.options?.[option] ?? null
    },

    /**
     * Returns true if this is an enterprise subscription
     */
    isEnterprise(): boolean {
      return subscriptionData.enterprise === true
    },

    /**
     * Returns true if subscription payment is past due
     */
    isPastDue(): boolean {
      return subscriptionData.billing_status === "past_due"
    },

    /**
     * Returns true if subscription is cancelled but still within grace period
     */
    isOnGracePeriod(): boolean {
      if (subscriptionData.ends_at === null) {
        return false
      }
      return moment.utc().isBefore(moment.utc(subscriptionData.ends_at))
    },

    /**
     * Returns true if subscription is in trial period
     */
    isTrialing(): boolean {
      if (subscriptionData.trial_ends_at === null) {
        return false
      }
      return moment.utc().isBefore(moment.utc(subscriptionData.trial_ends_at))
    },

    /**
     * Returns true if this is a free subscription
     */
    isFree(): boolean {
      return subscriptionData.name === "free"
    },

    /**
     * Returns true if subscription is active (not cancelled)
     */
    isActive(): boolean {
      return subscriptionData.ends_at === null
    },

    /**
     * Returns true if subscription is incomplete
     */
    isIncomplete(): boolean {
      return subscriptionData.billing_status === "incomplete"
    },

    /**
     * Returns true if subscription requires user action (past due or incomplete)
     */
    needsAction(): boolean {
      return that.isActive() && (that.isPastDue() || that.isIncomplete())
    },

    /**
     * Returns the period end date as a moment object
     */
    periodEndsAt(): moment.Moment | null {
      if (!subscriptionData.period_ends_at) {
        return null
      }
      return moment.utc(subscriptionData.period_ends_at).local()
    },

    /**
     * Returns the period start date as a moment object
     */
    periodStartsAt(): moment.Moment | null {
      if (!subscriptionData.period_starts_at) {
        return null
      }
      return moment.utc(subscriptionData.period_starts_at).local()
    },

    /**
     * Returns the trial end date as a moment object
     */
    trialEndsAt(): moment.Moment | null {
      if (!subscriptionData.trial_ends_at) {
        return null
      }
      return moment.utc(subscriptionData.trial_ends_at).local()
    },

    /**
     * Returns the subscription end date as a moment object
     */
    endsAt(): moment.Moment | null {
      if (!subscriptionData.ends_at) {
        return null
      }
      return moment.utc(subscriptionData.ends_at).local()
    },

    /**
     * Returns the next renewal date formatted as a string
     */
    nextRenewalDate(): string | null {
      if (!that.isActive()) {
        return null
      }
      const periodEnd = that.periodEndsAt()
      if (!periodEnd) {
        return null
      }
      return periodEnd.format("MMMM Do, YYYY")
    },

    /**
     * Returns the subscription end date formatted as a string
     */
    endDate(): string | null {
      if (that.isActive()) {
        return null
      }
      const endsAt = that.endsAt()
      if (!endsAt) {
        return null
      }
      return endsAt.format("MMMM Do, YYYY")
    },

    /**
     * Returns the number of days remaining in trial
     */
    remainingTrialDays(): number | null {
      if (!that.isTrialing()) {
        return null
      }
      const trialEnd = that.trialEndsAt()
      if (!trialEnd) {
        return null
      }
      const current = moment.utc()
      return Math.ceil(trialEnd.diff(current, "days", true))
    },

    /**
     * Returns the subscription group ("pro", "bulk") based on name
     */
    getGroup(): string | null {
      const name = subscriptionData.name
      if (!name) {
        return null
      }
      if (name.includes("pro")) {
        return "pro"
      }
      if (name.includes("bulk")) {
        return "bulk"
      }
      return null
    },

    /**
     * Navigate to the subscription settings page
     */
    visit(query: Record<string, unknown> = {}): void {
      visitOverride(that, query)
    },

    /**
     * Override the default visit behavior
     */
    setVisit(
      callback: (model: Subscription, query?: Record<string, unknown>) => void,
    ): Subscription {
      visitOverride = callback
      return that
    },
  }

  return that
}

/**
 * Creates a free subscription object (for users without a subscription)
 */
export function createFreeSubscription(orgSlug: string | null = null): Subscription {
  return SubscriptionModel(null, orgSlug)
}
